/**
 * Signs and forms you need (the owner, 2026-09-30: "We ought to have all forms needed linked and ready based on ingredients in
 * the fridge"; the photo: a Salt Lake County Health Department "May Contain Food Allergens" sign, English and Spanish side by side,
 * next to framed state licenses).
 *
 * The moment: the owner or manager, at the desk before opening or on the way to an inspection, asking "what must I post, and is it up?".
 * Expects a short list, each line a plain reason and one tap. The one action: open or print the sign, then tap "Posted".
 * Never a wall: the nudge on Today is one quiet card for managers and never blocks a safety flow.
 *
 * Gates (tested in signs.test.ts):
 * | S1 Sourced | a sign badged "Required" has a citation, a source URL and a quote; without one it is shown as "Recommended" at most |
 * | S2 State first, county second | a county item shows only for that county's health department; nothing is invented for a county |
 * | S3 From what is here | the allergen sign comes from the dishes and the food the place keeps: pantry, fridge, freezer, counter, food supplies, receiving log |
 * | S4 Honest names | the reason names the food ("You have peanut butter and almond milk, so ..."); a name that matches nothing never says "allergen-free" |
 * | S5 Two languages | every printable of ours has English and Spanish (signprint.ts); a government sign is linked, never copied or re-translated |
 * | S6 Who and when | "Posted" writes the signs_posted log row: sign, languages, where, who, when. Taking one down is a row too; the latest row wins |
 * | S7 Never blocks | the Today nudge counts required signs not marked posted; it is a card, not a gate |
 *
 * The catalog is in signs-catalog.ts, each row with the URL and the quote it rests on (docs/source/research/signs-and-forms-utah.md).
 */
import { allergensInName } from "./ingredientallergens.ts";
import { NINE, cleanAllergens, dishAllergens, shortLabel, type MenuAllergens } from "./allergens.ts";
import { advisoryDishes } from "./advisory.ts";
import { MENU_ITEMS } from "./playbooks.ts";
import { departmentFor } from "./localrules.ts";
import { SHAPES, postingsFor, shapesAt } from "./ruleshapes.ts";
import type { PostingShape } from "./states/shapes.ts";
import type { IsoDate, LogEntry, PermitType } from "./model.ts";
import { LANG_NAME, SIGN_ES_STATUS, printableFor, type PrintableId, type PrintableSign, type SignLang } from "./signprint.ts";
import { SIGN_CATALOG, signsCatalogFor } from "./signs-catalog.ts";
import type { UsState } from "./states/types.ts";

export const SIGNS_TEMPLATE = "signs_posted";
export const SIGNS_CHECKED = "2026-09-30" as const;

export type SignClass = "law" | "county" | "recommended";
export type SignKind = "food-code" | "license" | "employer" | "county";

export const CLASS_BADGE: Record<SignClass, string> = { law: "Required by law", county: "Required by your county", recommended: "Recommended" };
export const KIND_WORD: Record<SignKind, string> = { "food-code": "Food safety sign", license: "License or permit", employer: "Employer poster", county: "County posting" };

/** The page a rule rests on: where it was read, what it said, how sure we are. */
export interface SignSource {
  url: string;
  /** A short quote (30 words or fewer) from the page, word for word. */
  quote: string;
  /** MEASURED: the page or text was read on `lastChecked`. INFERRED: a best reading, not the page's own words. */
  evidence: "MEASURED" | "INFERRED";
  lastChecked: IsoDate;
}

/** A government document we link. We rehost none of them (see `rehost`). */
export interface OfficialDoc {
  /** "both": one file that carries English and Spanish together. */
  lang: SignLang | "both";
  label: string;
  url: string;
  /** Only shown to these health departments (the county's own document). Absent = every county. */
  departments?: readonly string[];
}

export interface SignDef {
  id: string;
  kind: SignKind;
  klass: SignClass;
  title: { en: string; es: string };
  /** Law, rule or county text the badge rests on. Required for law and county. */
  citation: string;
  source: SignSource;
  /** Only these health departments (LOCAL_DEPARTMENTS ids). Absent = the state rule, every county. */
  departments?: readonly string[];
  /** Only these permit types. Absent = every business permit (not a home kitchen unless listed). */
  permits?: readonly PermitType[];
  /** When the row applies: its allergens appear in the dishes or food ("allergens"), a dish can be raw or undercooked ("advisory"), a buffet is on the menu ("buffet"), or always. */
  rule: "always" | "allergens" | "advisory" | "buffet";
  /** Applies from this many people up. With the team unknown it is shown as a recommendation, never as required. */
  minStaff?: number;
  /** The trigger, in words. */
  trigger: string;
  /** The plain reason for an "always" row. */
  why?: string;
  /** One line about the document or the rule that the row's reader needs. */
  note?: string;
  /** What we have not been able to check, in one line. Shown on the row. */
  caveat?: string;
  official: readonly OfficialDoc[];
  /** May we host a copy? Always false unless the page's terms say so (none do, so far). */
  rehost: false;
  rehostNote: string;
  /** Our own content-only printable, where the law asks for the content and not a government form. */
  printable?: PrintableId;
  /** Where it usually hangs, as the default for the "Where?" box. */
  where: string;
  /** A line about the languages the official version comes in, when it is not both. */
  languageNote?: string;
  /** Does the sign carry an expiry we track (the permit)? */
  expires?: "permit" | "food-handler";
  /**
   * The posting shape this row is (rule shapes, states/<st>-shapes.ts postings): the row is listed only when that posting is in
   * force here with its condition met (alcohol served, a grade issued). Absent = not tied to a posting shape.
   */
  posting?: string;
  /** A California Prop 65 warning (states/ca-prop65.ts): printed with the regulation's exact words at its minimum type sizes. */
  prop65?: import("./states/ca-prop65.ts").Prop65Kind;
}

/* ─────────────── what the place has: dishes, food, supplies, receiving ─────────────── */

export type FoodFrom = "dish" | "pantry" | "fridge" | "freezer" | "counter" | "supply" | "receiving";
export interface IngredientSeen { name: string; from: FoodFrom; /** A dish whose allergens the owner confirmed. */ confirmed?: boolean }

export interface AllergenPresence { id: string; label: string; because: IngredientSeen[] }

/** The plain sentence for a list of names: "peanut butter and almond milk", "A, B and 2 more". */
export function nameList(names: readonly string[], max = 3): string {
  const u = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
  if (u.length <= max) return u.length <= 1 ? u.join("") : `${u.slice(0, -1).join(", ")} and ${u.at(-1)}`;
  return `${u.slice(0, max).join(", ")} and ${u.length - max} more`;
}

export interface PlaceFacts {
  /** The place's state (states/index.ts placeState). Absent = Utah. */
  state?: UsState;
  county?: string;
  permitType: PermitType;
  menuItemIds: readonly string[];
  menuAllergens?: MenuAllergens;
  /** Food names the place keeps: from the food store, food supplies and the receiving log. */
  foods?: readonly IngredientSeen[];
  /** Active people at this place, when the caller could read the team (managers). */
  staffCount?: number;
  /** Serves a highly susceptible population (changes the advisory: Food Code 3-801.11). */
  servesHsp?: boolean;
  /** Sells alcohol by the drink (Location.alcoholServed): the posting condition `alcoholServed`. Absent = not answered. */
  alcoholServed?: boolean;
  /** The owner said the business has 10 or more employees (Location.staffTenOrMore): counts for Prop 65 even when the roster is short. */
  staffTenOrMore?: boolean;
  permitNumber?: string;
  permitExpiresOn?: IsoDate;
  /** Days to the soonest food handler permit expiry among active people (onboarding.permitExpiresInDays), when known. */
  handlerPermitDays?: readonly (number | null)[];
  today: IsoDate;
  business?: string;
}

/**
 * Which of the nine this place's dishes and food point to, each with the things it comes from (S3). A dish counts by what the
 * owner confirmed; an unconfirmed dish counts by what such dishes often have (and is marked unconfirmed). A food counts by its name
 * (ingredientallergens.ts), which is a hint, never proof.
 */
export function presentAllergens(p: Pick<PlaceFacts, "menuItemIds" | "menuAllergens" | "foods">): AllergenPresence[] {
  const seen = new Map<string, IngredientSeen[]>();
  const add = (id: string, s: IngredientSeen) => seen.set(id, [...(seen.get(id) ?? []), s]);
  for (const m of MENU_ITEMS.filter((x) => p.menuItemIds.includes(x.id))) {
    const d = dishAllergens({ menuAllergens: p.menuAllergens }, m);
    for (const id of d.confirmed ? d.contains : d.often) add(id, { name: m.name, from: "dish", confirmed: d.confirmed });
  }
  for (const f of p.foods ?? []) for (const h of allergensInName(f.name)) add(h.allergen, f);
  return NINE.filter((id) => seen.has(id)).map((id) => ({ id, label: shortLabel(id), because: seen.get(id)! }));
}

/** "You have peanut butter and almond milk, so the allergen sign applies." Names foods first, dishes after. */
export function allergenWhy(present: readonly AllergenPresence[]): string {
  const all = present.flatMap((a) => a.because);
  const foods = all.filter((s) => s.from !== "dish").map((s) => s.name.toLowerCase());
  const dishes = all.filter((s) => s.from === "dish").map((s) => s.name);
  const labels = present.map((a) => a.label.toLowerCase());
  if (foods.length && dishes.length) return `You have ${nameList(foods)} and serve ${nameList(dishes)}, so ${nameList(labels, 9)} ${labels.length === 1 ? "is" : "are"} in your food and the allergen sign applies.`;
  if (foods.length) return `You have ${nameList(foods)}, so the allergen sign applies (${nameList(labels, 9)}).`;
  return `You serve ${nameList(dishes)}, so the allergen sign applies (${nameList(labels, 9)}).`;
}

/* ─────────────── the posting record ─────────────── */

export interface SignPosting {
  signId: string;
  status: "posted" | "taken down";
  languages: string;
  where: string;
  date: IsoDate;
  time: string;
  initials: string;
  /** Who, by the account that wrote the row, when the caller can name it. */
  by?: string;
  at: string;
}
export const BOTH_LANGS = "English and Spanish";
type PostRow = Pick<LogEntry, "templateId" | "date" | "createdAt" | "values"> & { createdBy?: string; correction?: unknown };

/** The row a "Posted" tap writes (S6). A missing initials box is refused: a posting with no person is not a record. */
export function signRowValues(o: { signId: string; status?: "posted" | "taken down"; languages?: string; where?: string; date: IsoDate; time: string; initials: string; /** The place's state: its own rows (Nevada's pregnancy warning) are found only in its list. */ state?: UsState }): { values: Record<string, string> } {
  const def = signById(o.signId, o.state);
  if (!def) throw new Error("That sign is not on the list.");
  if (!o.initials.trim()) throw new Error("Who put it up is missing.");
  const where = (o.where ?? "").trim().slice(0, 80);
  return { values: { date: o.date, time: o.time, sign: def.title.en, languages: o.languages ?? (def.printable || def.official.some((d) => d.lang === "es") ? BOTH_LANGS : LANG_NAME.en), where, status: o.status ?? "posted", initials: o.initials.trim() } };
}

/** The latest row of each sign. A row for a sign we do not list is ignored; a corrected row does not count. */
export function signPostings(rows: readonly PostRow[], state?: UsState): Map<string, SignPosting> {
  const out = new Map<string, SignPosting>();
  const defs = state ? signDefsFor(state) : SIGN_CATALOG;
  const mine = rows.filter((r) => r.templateId === SIGNS_TEMPLATE && !r.correction).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const r of mine) {
    const def = defs.find((d) => d.title.en === String(r.values?.sign ?? ""));
    const status = String(r.values?.status ?? "");
    if (!def || (status !== "posted" && status !== "taken down")) continue;
    out.set(def.id, { signId: def.id, status, languages: String(r.values?.languages ?? ""), where: String(r.values?.where ?? ""), date: r.date, time: String(r.values?.time ?? ""), initials: String(r.values?.initials ?? ""), ...(r.createdBy ? { by: r.createdBy } : {}), at: r.createdAt });
  }
  return out;
}

/** The signs up now (last mark "posted"), with the day: evidence on the inspector's form (inspector.ts). */
export function postedSignsOf(rows: readonly PostRow[], state?: UsState): { id: string; date: IsoDate }[] {
  return [...signPostings(rows, state).values()].filter((p) => p.status === "posted").map((p) => ({ id: p.signId, date: p.date }));
}

/* ─────────────── the list ─────────────── */

export interface SignNeed {
  id: string;
  title: { en: string; es: string };
  kind: SignKind;
  klass: SignClass;
  /** "Required by law" etc.; only ever shown with the citation (S1). */
  badge: string;
  citation: string;
  source: SignSource;
  /** Plain words: why this place needs it. */
  why: string;
  official: readonly OfficialDoc[];
  note?: string;
  caveat?: string;
  /** The Spanish side: "official English and Spanish", "official English only, our Spanish companion", or "ours, English and Spanish". */
  languages: { en: boolean; es: boolean; line: string; esOfficial: boolean };
  printable?: PrintableSign;
  /** A Prop 65 warning: the screen prints it (8½ × 11 and 5½ × 5½) with packages/pdf renderProp65Pdf. */
  prop65?: import("./states/ca-prop65.ts").Prop65Kind;
  where: string;
  posting?: SignPosting;
  posted: boolean;
  /** For a permit: the expiry from the record, and what it means today. */
  expiresOn?: IsoDate;
  expiry?: "ok" | "soon" | "expired";
  expiryLine?: string;
  /** A required item not marked posted (or whose permit is out of date). */
  open: boolean;
  /** The head count the row depends on, not yet known: the screen offers one tap, "We have 10 or more" (Location.staffTenOrMore). */
  askStaff?: number;
}

/**
 * A posting the state's pack requires (rule shapes) with no catalog row of its own, as a row: New York's choking poster, menu
 * allergen line, staff allergen notice and "No Smoking" signs, NYC's grade card; California's inspection-report notice and county
 * grade cards. Its citation, page and quote are the posting's own (states/<st>-shapes.ts). A posting set by a health authority is a
 * "county" row. Utah's and Nevada's postings all have catalog rows, so their lists are unchanged.
 */
function postingSignDef(p: PostingShape, fromAuthority: boolean): SignDef {
  const what = p.what.trim();
  // The title is the posting's own words up to where it says where or how ("Your letter grade card, within 5 feet of ..." → "Your letter grade card").
  const title = what.replace(/\s*\([^)]*\)/g, "").split(/;|: (?=\S)|, (?=where|within|at|in|near|online|on|only|as|next)\b/)[0]!.replace(/\.$/, "").trim();
  return {
    id: `posting-${p.id}`, kind: fromAuthority ? "county" : "food-code", klass: p.level === "recommended" ? "recommended" : fromAuthority ? "county" : "law",
    title: { en: title, es: title },
    citation: p.source.citation,
    source: { url: p.source.url, quote: p.source.quote ?? "", evidence: p.source.quote ? "MEASURED" : "INFERRED", lastChecked: p.source.lastChecked },
    permits: ["food_establishment", "temporary_event", "mehk", "private_members_only", "mobile_food_business"],
    // The full line only when it says more than the title (where it goes, what it says); never the title twice.
    rule: "always", trigger: what, why: what.replace(/\.$/, "") === title ? (p.level === "recommended" ? "We recommend it here." : "The law here asks for it.") : what, posting: p.id, ...(p.minEmployees ? { minStaff: p.minEmployees } : {}),
    official: [], rehost: false, rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
    where: "",
  };
}
const POSTING_DEFS = new Map<UsState, SignDef[]>();
/** The state's postings with no catalog row, as rows (state layer first, then each authority's). */
function postingSignDefs(state?: UsState): SignDef[] {
  const st = state ?? "UT";
  let v = POSTING_DEFS.get(st);
  if (!v) {
    const covered = new Set(signsCatalogFor(st).flatMap((d) => [d.id, d.posting].filter((x): x is string => !!x)));
    const sh = SHAPES[st];
    const all: [PostingShape, boolean][] = [...sh.state.postings.map((p) => [p, false] as [PostingShape, boolean]), ...Object.values(sh.authorities).flatMap((a) => (a.postings ?? []).map((p) => [p, true] as [PostingShape, boolean]))];
    // A posting that is a catalog row under another name (Florida's "handsink-sign" is the Food Code's 6-301.14 handwashing
    // sign) isn't listed twice: the catalog row carries it (signsCatalogAt gives it the state's citation).
    const sameSection = (p: PostingShape) => signsCatalogFor(st).some((d) => sharesSection(d.citation, p.source.citation));
    const seen = new Set<string>();
    v = all.filter(([p]) => !covered.has(p.id) && !sameSection(p) && !seen.has(p.id) && !!seen.add(p.id)).map(([p, auth]) => postingSignDef(p, auth));
    POSTING_DEFS.set(st, v);
  }
  return v;
}
/** Two citations name the same Food Code section ("6-301.14"). */
const FC_SECTION = /\b\d-\d{3}\.\d{2,3}\b/g;
const sharesSection = (a: string, b: string) => { const x = new Set(a.match(FC_SECTION) ?? []); return (b.match(FC_SECTION) ?? []).some((y) => x.has(y)); };
const CATALOG_AT = new Map<UsState, SignDef[]>();
/**
 * The catalog with the state's own citation on a Food Code row where its pack posts the same section under another id: Florida's
 * handwashing sign cites "FDA Food Code 2017 6-301.14 (rule 61C-1.001)", the edition Florida adopted, not the 2022 model.
 */
function signsCatalogAt(state?: UsState): SignDef[] {
  const st = state ?? "UT";
  let v = CATALOG_AT.get(st);
  if (!v) {
    const cat = signsCatalogFor(st);
    const ids = new Set(cat.flatMap((d) => [d.id, d.posting].filter((x): x is string => !!x)));
    const sh = SHAPES[st];
    const own = sh.state.postings.filter((p) => !ids.has(p.id) && p.source.quote);
    v = cat.map((d) => {
      const p = own.find((x) => sharesSection(d.citation, x.source.citation));
      return p ? { ...d, citation: p.source.citation, source: { url: p.source.url, quote: p.source.quote!, evidence: "MEASURED" as const, lastChecked: p.source.lastChecked } } : d;
    });
    CATALOG_AT.set(st, v);
  }
  return v;
}
/** Every row a state can list: its catalog (with its own citations), then its postings that have no catalog row. */
export const signDefsFor = (state?: UsState): SignDef[] => [...signsCatalogAt(state), ...postingSignDefs(state)];

export const signById = (id: string, state?: UsState): SignDef | undefined => signDefsFor(state).find((d) => d.id === id);

/** Does the catalog row apply to this place (S2)? State rows: yes for every county. County rows: only that department's counties. */
export function appliesHere(d: SignDef, p: Pick<PlaceFacts, "county" | "permitType">, state?: UsState): boolean {
  if (d.permits ? !d.permits.includes(p.permitType) : p.permitType === "mehk") return false;
  if (d.departments) {
    const dep = departmentFor(state ?? "UT", p.county);
    if (!dep || !d.departments.includes(dep.id)) return false;
  }
  return true;
}

const dayDiff = (a: IsoDate, b: IsoDate) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
const addDays = (d: IsoDate, n: number): IsoDate => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const dayText = (d: IsoDate) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/** The trigger for each rule: a plain reason, or null when it does not apply to this place. */
export function reasonFor(d: SignDef, p: PlaceFacts, present: readonly AllergenPresence[]): string | null {
  switch (d.rule) {
    case "allergens": return present.length ? allergenWhy(present) : null;
    case "advisory": {
      const dishes = advisoryDishes(p.menuItemIds).map((m) => m.name);
      if (dishes.length) return `You serve ${nameList(dishes)}, which can be served raw or undercooked, so the consumer advisory applies.`;
      return p.servesHsp ? "You serve a highly susceptible population, so raw and undercooked animal food is not served; check the notice anyway." : null;
    }
    case "buffet": return p.menuItemIds.includes("buffet-line") ? d.why ?? d.trigger : null;
    default: return d.why ?? d.trigger;
  }
}

/**
 * The class a row may wear (S1): "law" or "county" only with a citation, a URL, a quote and a page that was actually read (MEASURED), and, for
 * a size line, a known team. Anything less is shown as "recommended".
 */
export function classOf(d: Pick<SignDef, "klass" | "citation" | "source">, sizeUnknown = false): SignClass {
  const sourced = !!(d.citation.trim() && d.source.url.trim() && d.source.quote.trim() && d.source.evidence === "MEASURED") && !sizeUnknown;
  return d.klass !== "recommended" && !sourced ? "recommended" : d.klass;
}

/** Every sign or form this place needs, in a useful order (required first), each with its reason and posting state (S1 to S7). */
export function signsFor(p: PlaceFacts, rows: readonly PostRow[] = [], st?: UsState): SignNeed[] {
  const state = st ?? p.state;
  const present = presentAllergens(p);
  const posted = signPostings(rows, state ?? "UT");
  const dept = departmentFor(state ?? "UT", p.county)?.id;
  // The posting shapes in force here, with their conditions (ruleshapes.ts postingsFor): alcohol served, a grade issued.
  // Head count for a size-bound posting (California's Prop 65, 10 or more: HSC 25249.11(b)), counted from the roster
  // (coordinator 2026-10-03, B=MAT: no setup question). A roster below the line is often just the owner while hiring, so it
  // isn't proof of fewer: the row stays "applies if you have 10 or more" with one tap, "We have 10 or more". That tap counts.
  const sized = p.staffTenOrMore ? Math.max(p.staffCount ?? 0, 10) : p.staffCount !== undefined && p.staffCount >= 10 ? p.staffCount : undefined;
  const postings = new Set(postingsFor(shapesAt({ state: state ?? "UT", county: p.county }), { alcoholServed: p.alcoholServed === true, permit: p.permitType, ...(sized !== undefined ? { employees: sized } : {}) }).map((x) => x.id));
  const needs: SignNeed[] = [];
  for (const d of signDefsFor(state)) {
    if (!appliesHere(d, p, state)) continue;
    if (d.posting && !postings.has(d.posting)) continue;
    let why = reasonFor(d, p, present);
    if (!why) continue;
    // A size line (15 or more people): with the team unknown it is a reminder, not a requirement. Below the line it does not apply.
    // A Prop 65 row (or a pack posting with a head count) reads the roster-or-tap count; any other size line is unchanged.
    const headCount = d.prop65 || (d.posting && d.minStaff !== undefined) ? (sized !== undefined && sized >= d.minStaff! ? sized : undefined) : p.staffCount;
    const sizeUnknown = d.minStaff !== undefined && headCount === undefined;
    if (d.minStaff !== undefined && headCount !== undefined && headCount < d.minStaff) continue;
    if (sizeUnknown) why = (d.prop65 || d.posting) && p.staffCount !== undefined
      ? `${why} It applies with ${d.minStaff} or more employees; your team list here has ${p.staffCount}.`
      : `${why} (We could not count your team here.)`;
    // S1: no law or county badge without a citation, a URL and a quote. A row missing them is shown as a recommendation.
    // The badge also needs the page to have been read (MEASURED) and, for a size line, a known team.
    const klass = classOf(d, sizeUnknown);
    const printable = d.printable ? printableFor(d.printable, { business: p.business, allergens: present.map((a) => a.id) }) : undefined;
    const official = d.official.filter((o) => !o.departments || (dept !== undefined && o.departments.includes(dept)));
    const esOfficial = official.some((o) => o.lang === "es" || o.lang === "both");
    const hasOfficial = official.length > 0;
    const languages = {
      en: true, es: !!printable || esOfficial, esOfficial,
      line: printable && hasOfficial ? (esOfficial ? "Official sign in English and Spanish. Ours too, in both." : "Official sign in English only. Our own companion is in English and Spanish.")
        : printable ? "Ours, in English and Spanish. The Spanish still needs a native speaker's review."
        : esOfficial ? "Official, in English and Spanish."
        : d.languageNote ?? (hasOfficial ? "Official version in English only; no Spanish version found." : "Issued or written by the government, in its own language. We add nothing to it."),
    };
    const posting = posted.get(d.id);
    let expiresOn: IsoDate | undefined, expiry: SignNeed["expiry"], expiryLine: string | undefined;
    if (d.expires === "permit" && p.permitExpiresOn) { expiresOn = p.permitExpiresOn; }
    if (d.expires === "food-handler") {
      const ds = (p.handlerPermitDays ?? []).filter((x): x is number => typeof x === "number");
      if (ds.length) expiresOn = addDays(p.today, Math.min(...ds));
    }
    if (expiresOn) {
      const left = dayDiff(p.today, expiresOn);
      expiry = left < 0 ? "expired" : left <= 30 ? "soon" : "ok";
      expiryLine = left < 0 ? `Expired ${dayText(expiresOn)}. Renew it.` : left <= 30 ? `Expires ${dayText(expiresOn)} (${left} day${left === 1 ? "" : "s"}).` : `Good until ${dayText(expiresOn)}.`;
    }
    const isPosted = posting?.status === "posted";
    needs.push({
      id: d.id, title: d.title, kind: d.kind, klass, badge: CLASS_BADGE[klass], citation: d.citation, source: d.source,
      why, official, ...(d.note ? { note: d.note } : {}), ...(d.caveat ? { caveat: d.caveat } : {}), languages, ...(printable ? { printable } : {}), ...(d.prop65 ? { prop65: d.prop65 } : {}), where: posting?.where || d.where, ...(posting ? { posting } : {}), posted: isPosted,
      ...(expiresOn ? { expiresOn, expiry, expiryLine } : {}),
      open: klass !== "recommended" && (!isPosted || expiry === "expired"),
      ...(sizeUnknown && (d.prop65 || d.posting) ? { askStaff: d.minStaff! } : {}),
    });
  }
  const rank = (n: SignNeed) => (n.klass === "law" ? 0 : n.klass === "county" ? 1 : 2) * 10 + (n.kind === "food-code" ? 0 : n.kind === "county" ? 1 : n.kind === "license" ? 2 : 3);
  return needs.sort((a, b) => rank(a) - rank(b));
}

/** The one-line nudge for Today (S7): how many required signs are not marked posted. null = nothing to say. */
export function signsNudge(needs: readonly SignNeed[]): { count: number; names: string[]; line: string } | null {
  const open = needs.filter((n) => n.open);
  if (!open.length) return null;
  const names = open.map((n) => n.title.en);
  return { count: open.length, names, line: open.length === 1 ? `${names[0]} is not marked as posted.` : `${open.length} required signs are not marked as posted: ${nameList(names, 2)}.` };
}

export const SIGNS_CARD = {
  title: "Signs and forms you need",
  lead: "Worked out from your county, your kind of business, your menu and the food you keep.",
  open: "See signs and forms",
  print: "Print both languages",
  printEn: "English only",
  printEs: "Español solo",
  /** The PDF (with the QR and "Made with" line), for a print shop or to email. */
  downloadPdf: "Download the PDF",
  /** California Prop 65 (27 CCR 25607.5): the door sign (8½ × 11, 28-point+) and the register card (5½ × 5½, 20-point+). */
  printProp65Door: "Print the door sign (8½ × 11)",
  printProp65Register: "Print the register card (5½ × 5½)",
  prop65PhoneLine: "Print this sign from Signs and forms on the website: the door sign (8½ × 11) or the register card (5½ × 5½).",
  posted: "Posted",
  where: "Where is it?",
  takeDown: "Taken down",
  recordNote: "This says someone put it up, and who and when. It does not check the sign.",
  spanishNote: `Our Spanish is ${SIGN_ES_STATUS} by a native speaker.`,
  none: "Nothing on the list needs posting for this place yet.",
  nudge: "Signs and forms",
  seeAll: "See signs and forms",
  notCounty: "We list a county's own sign only when we found its page. None found is not the same as none required: ask your health department.",
  govNote: "Government signs are linked from the government's own page. We never copy them or put our logo on them.",
} as const;

export { cleanAllergens };

/* ─────────────── gathering what the place has (S3) ─────────────── */

type FoodLike = { name: string; place: string };
type EntryLike = Pick<LogEntry, "templateId" | "date" | "values">;
type SupplyLike = { name: string; kind: string };

/** How far back a receiving line still counts as "food you have": a delivery is used up within weeks. */
export const RECEIVING_DAYS = 45;

/**
 * Everything the place keeps that has a name: the food store (pantry, fridge, freezer, counter; not "gone"), supplies of kind food, and
 * accepted lines of the receiving log in the last RECEIVING_DAYS days. Photo-read food lands in the food store, so it is in here too.
 * The same food named twice (fridge and receiving) is one line.
 */
export function ingredientsSeen(o: { foods?: readonly FoodLike[]; supplies?: readonly SupplyLike[]; entries?: readonly EntryLike[]; today: IsoDate }): IngredientSeen[] {
  const out: IngredientSeen[] = [];
  const seen = new Set<string>();
  const push = (name: string, from: FoodFrom) => {
    const k = name.trim().toLowerCase();
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push({ name: name.trim(), from });
  };
  for (const f of o.foods ?? []) if (f.place !== "gone") push(f.name, (["pantry", "fridge", "freezer", "counter"].includes(f.place) ? f.place : "pantry") as FoodFrom);
  for (const s of o.supplies ?? []) if (s.kind === "food") push(s.name, "supply");
  const since = addDays(o.today, -RECEIVING_DAYS);
  for (const e of o.entries ?? []) {
    if (e.templateId !== "receiving" || e.date < since || String(e.values?.result ?? "accept") === "reject") continue;
    push(String(e.values?.item ?? ""), "receiving");
  }
  return out;
}

/** The facts the rules read, from the records every client already loads. */
export function placeFactsOf(o: {
  location: { state?: UsState; county: string; permitType: PermitType; menuItemIds: string[]; menuAllergens?: MenuAllergens; servesHsp?: boolean; alcoholServed?: boolean; staffTenOrMore?: boolean; permitNumber?: string; permitExpiresOn?: IsoDate; supplies?: readonly SupplyLike[] };
  today: IsoDate; business?: string; foods?: readonly FoodLike[]; entries?: readonly EntryLike[];
  /** Active people at this place. Undefined when the viewer may not read the team. */
  team?: readonly { permitExpiresInDays?: number | null }[];
}): PlaceFacts {
  const l = o.location;
  return {
    ...(l.state ? { state: l.state } : {}), county: l.county, permitType: l.permitType, menuItemIds: l.menuItemIds, menuAllergens: l.menuAllergens,
    foods: ingredientsSeen({ foods: o.foods, supplies: l.supplies, entries: o.entries, today: o.today }),
    ...(o.team ? { staffCount: o.team.length, handlerPermitDays: o.team.map((t) => t.permitExpiresInDays ?? null) } : {}),
    ...(l.servesHsp ? { servesHsp: true } : {}),
    ...(l.alcoholServed ? { alcoholServed: true } : {}),
    ...(l.staffTenOrMore ? { staffTenOrMore: true } : {}),
    ...(l.permitNumber ? { permitNumber: l.permitNumber } : {}), ...(l.permitExpiresOn ? { permitExpiresOn: l.permitExpiresOn } : {}),
    today: o.today, ...(o.business ? { business: o.business } : {}),
  };
}

/* ─────────────── the binder page ─────────────── */

/**
 * The binder's "Signs posted" page: every sign or form this place needs, whether it is marked posted, in which languages, where, who and when.
 * A required sign with no posting row reads "NOT MARKED POSTED" (never "missing": the sign may be up and nobody tapped). It says what it is:
 * a record of what a person said, not a check of the sign.
 */
export function signsSection(needs: readonly SignNeed[], names: Readonly<Record<string, string>>, timeLabel: (iso: string) => string, withPhoto: readonly string[] = []): import("./binder.ts").BinderSection {
  const who = (p?: SignPosting) => (p?.by ? names[p.by] : "") || p?.initials || "";
  return {
    id: "signs",
    heading: "Signs posted",
    intro: "The signs and forms this place needs, from its county, kind of business, menu and the food it keeps, and whether each is marked posted. A mark says someone put it up and when; it does not check the sign. Government signs are the government's own.",
    empty: "No signs or forms are listed for this place yet.",
    table: needs.length ? {
      columns: ["Sign or form", "Why it is needed", "Languages", "Where", "Marked", "By"], widths: [2, 3, 1.2, 1.2, 1.5, 0.7],
      rows: needs.map((n) => {
        const p = n.posting;
        const marked = n.posted && p ? `Posted ${timeLabel(p.at)}${withPhoto.includes(n.id) ? ", with a photo" : ""}` : p?.status === "taken down" ? `Taken down ${timeLabel(p.at)}` : n.klass === "recommended" ? "Not marked (recommended)" : "NOT MARKED POSTED";
        const lang = n.posted && p ? p.languages : n.languages.es ? "English and Spanish" : "English";
        return {
          cells: [`${n.title.en} (${n.badge}; ${n.citation})`, `${n.why}${n.expiryLine ? ` ${n.expiryLine}` : ""}`, lang, n.posted ? p!.where : "", marked, n.posted ? who(p) : ""],
          tone: n.open ? ("bad" as const) : n.posted ? ("ok" as const) : undefined,
        };
      }),
    } : undefined,
  };
}

/** Active people at one place with days to their food handler permit's expiry, from the stored credentials (server and demo; the web screen uses onboarding). */
export function teamForSigns(employees: readonly { id: string; active: boolean; locationIds: readonly string[] }[], credentials: readonly { employeeId: string; kind: string; expiresOn?: IsoDate; verifiedAt?: string }[], locationId: string, today: IsoDate): { permitExpiresInDays: number | null }[] {
  return employees.filter((e) => e.active && (e.locationIds.length === 0 || e.locationIds.includes(locationId))).map((e) => {
    const c = credentials.filter((x) => x.employeeId === e.id && x.kind === "food_handler_permit" && x.verifiedAt && x.expiresOn).sort((a, b) => (b.expiresOn ?? "").localeCompare(a.expiresOn ?? ""))[0];
    return { permitExpiresInDays: c?.expiresOn ? dayDiff(today, c.expiresOn) : null };
  });
}
